import { useState } from 'react';
import { Search } from 'lucide-react';
import { formatCopy } from '@/i18n/ns';
import type { WelcomeCopy } from '@/i18n/ns/welcome';
import { ConnectorCard } from '@/workspace/connector-card';
import { connectionKey } from '@/workspace/connection-api';
import type { useConnections } from '@/workspace/use-connections';
import { pipedreamAppsForFocus, toolIdsForFocus, toolSummary } from './onboarding-tools';
import { suggestedFocuses, type Focus, type Role } from './preferences';
import { connectionOptions } from './connection-options';
import { PipedreamCatalogue } from '@/workspace/pipedream-catalogue';
import { ConnectionFeedback } from '@/workspace/connection-feedback';
import { ConnectSheetHost } from '@/workspace/connect-access/connect-sheet';

// Every string here comes from the `welcome` namespace, so a person who uses
// Poneva in Swedish never meets an English step. The shared connector card,
// catalogue and feedback keep the Connections surface's own copy.
export function ConnectTools({
  copy,
  connections,
  companyName,
  interests,
  role,
  suggestion = false,
}: {
  copy: WelcomeCopy;
  connections: ReturnType<typeof useConnections>;
  companyName: string;
  interests: Focus[];
  role: Role | null;
  suggestion?: boolean;
}) {
  const [accounts, setAccounts] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [browse, setBrowse] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data, error, busy, notice, connectLink, activate, requestConnect, disconnect, retry } =
    connections;
  const guidedFocuses = suggestedFocuses(interests, role);
  const recommended = toolIdsForFocus(guidedFocuses);
  const groups = connectionOptions(data?.items ?? []);
  const scoped = groups.map(
    ({ primary, options }) =>
      options.find((item) => connectionKey(item) === accounts[primary.id]) ?? primary,
  );
  const listed = data?.pipedreamAvailable
    ? scoped.filter(
        (item) =>
          item.id.startsWith('pipedream:') ||
          !['not_connected', 'not_configured'].includes(item.status),
      )
    : scoped;
  const candidates = suggestion
    ? listed.filter((item) => item.actions.connect.available || item.status === 'connected')
    : listed;
  const suggested = candidates.filter((item) => recommended.includes(item.id));
  const visible = browse
    ? listed.filter((item) =>
        (item.name + ' ' + item.description).toLowerCase().includes(query.trim().toLowerCase()),
      )
    : (suggested.length ? suggested : candidates).slice(0, suggestion ? 2 : 3);
  const setup = data?.pipedreamAvailable ? (data.pipedreamSetup ?? data.setup) : data?.setup;
  return (
    <section className="onboarding-connect" aria-label={copy.toolsLabel}>
      <ConnectionFeedback connections={connections} />
      <ConnectSheetHost connections={connections} companyName={companyName} />
      {!suggestion && !data?.pipedreamAvailable && (
        <div className="onboarding-catalogue-bar">
          <span>{browse ? copy.allTools : copy.suggestedTools}</span>
          <button
            className="company-link"
            aria-expanded={browse}
            onClick={() => {
              setBrowse(!browse);
              setQuery('');
            }}
          >
            {browse ? copy.backToSuggestions : copy.browseAllTools}
          </button>
        </div>
      )}
      {browse && (
        <label className="onboarding-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            aria-label={copy.searchTools}
            placeholder={copy.findTool}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      )}
      {notice && (
        <p className="ws-connection-notice" role="status">
          {notice}
        </p>
      )}
      {connectLink && (
        <p className="ws-connection-notice" role="status">
          {copy.popupBlocked}{' '}
          <a href={connectLink} target="_blank" rel="noreferrer">
            {copy.openAuthorization}
          </a>
        </p>
      )}
      {busy && (
        <p className="onboarding-scope" role="status">
          {copy.authorizing}
          <button className="company-link" onClick={connections.cancel}>
            {copy.stopWaiting}
          </button>
        </p>
      )}
      {error ? (
        <div role="alert">
          {/* The hook's raw failure text ("Failed to fetch") never reaches the welcome. */}
          <p>{copy.connectionsFailed}</p>
          <button className="company-secondary" onClick={retry}>
            {copy.tryAgain}
          </button>
        </div>
      ) : !data ? (
        <p role="status">{copy.loadingConnections}</p>
      ) : (
        <>
          {setup && !setup.available && <p className="onboarding-scope">{copy.toolsUnavailable}</p>}
          <div className={'onboarding-connector-grid' + (browse ? ' is-browsing' : '')}>
            {visible
              .filter(
                (item) =>
                  !data.pipedreamAvailable ||
                  item.status === 'connected' ||
                  item.actions.connect.available,
              )
              .map((item) => (
                <div key={item.id}>
                  <ConnectorCard
                    key={connectionKey(item)}
                    item={item}
                    summary={toolSummary(item.id, copy)}
                    busy={busy === connectionKey(item)}
                    open={expanded === connectionKey(item)}
                    onActivate={() => {
                      if (item.actions.connect.available) {
                        setExpanded(null);
                        activate(item);
                      } else {
                        setExpanded((current) =>
                          current === connectionKey(item) ? null : connectionKey(item),
                        );
                      }
                    }}
                    onReconnect={() => requestConnect(item)}
                    onDisconnect={() => void disconnect(item)}
                  />
                  {expanded === connectionKey(item) &&
                    groups
                      .find(({ primary }) => primary.id === item.id)!
                      .options.filter(
                        (option) =>
                          connectionKey(option) !== connectionKey(item) &&
                          (option.status === 'connected' || option.actions.connect.available),
                      )
                      .map((option) => (
                        <button
                          key={connectionKey(option)}
                          className="company-link onboarding-account-option"
                          disabled={Boolean(busy)}
                          onClick={() => {
                            setAccounts({ ...accounts, [item.id]: connectionKey(option) });
                            setExpanded(connectionKey(option));
                          }}
                        >
                          {option.accountLabel ||
                            (option.audience === 'private'
                              ? copy.useMyAccount
                              : formatCopy(copy.useCompanyAccount, { company: companyName }))}
                        </button>
                      ))}
                  {item.actions.connect.available &&
                    groups
                      .find(({ primary }) => primary.id === item.id)!
                      .options.some(
                        (option) =>
                          connectionKey(option) !== connectionKey(item) &&
                          option.actions.connect.available,
                      ) &&
                    expanded !== connectionKey(item) && (
                      <button
                        className="company-link onboarding-account-option"
                        disabled={Boolean(busy)}
                        onClick={() => setExpanded(connectionKey(item))}
                      >
                        {copy.chooseAudience}
                      </button>
                    )}
                </div>
              ))}
          </div>
          {data.pipedreamAvailable && (
            <PipedreamCatalogue
              compact
              suggestionLimit={suggestion ? 2 : 6}
              preferredApps={pipedreamAppsForFocus(guidedFocuses)}
              onConnect={activate}
              busy={busy}
              canConnect={Boolean(setup?.available)}
              companyName={companyName}
              connectedApps={data.items
                .filter((item) => item.status === 'connected' && item.id.startsWith('pipedream:'))
                .map((item) => item.id.slice(10))}
            />
          )}
          {!visible.length && !data.pipedreamAvailable && (
            <p className="onboarding-empty">{query ? copy.noToolsMatch : copy.noToolsYet}</p>
          )}
        </>
      )}
    </section>
  );
}
